import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";
import GuidelinesModal from "../components/guidelines";
import { talkImageMode, slideImageMode, LAYOUT_MODES } from "../sectionLayout";

const LAYOUT_LABELS = { both: "Image + text", text: "Text only", image: "Image only" };

// Small 3-way segmented control (Image + text / Text only / Image only)
function LayoutSegControl({ value, onChange }) {
    return (
        <div className="seg-control" role="group">
            {LAYOUT_MODES.map((mode) => (
                <button
                    key={mode}
                    type="button"
                    className={value === mode ? "is-active" : ""}
                    aria-pressed={value === mode}
                    onClick={() => onChange(mode)}
                >
                    {LAYOUT_LABELS[mode]}
                </button>
            ))}
        </div>
    );
}

// Quill configuration
const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        ["blockquote", "link"],
        ["clean"],
    ],
};

const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "blockquote",
    "link",
    "script",
    "clean",
];

export function SectionEditor({
    section,
    sectionType,
    index,
    displayIndex,
    total,
    onChange,
    onRemove,
    onMoveUp,
    onMoveDown,
    onAddCommentToSection,
    comments = [],
    onDeleteComment,
}) {
    const number = (typeof displayIndex === "number" ? displayIndex : index) + 1;
    const hasCustomSlide = typeof section?.slideBody === "string" && section.slideBody !== null;
    const [slideOpen, setSlideOpen] = useState(hasCustomSlide);

    const openSlideEditor = () => {
        if (!slideOpen) {
            if (!hasCustomSlide) {
                // Initialize slideBody from body; if legacy bodies[] exist, migrate them into slideBodies
                onChange(index, "slideBody", section?.body || "");
                const legacyBodies = Array.isArray(section?.bodies) ? section.bodies.filter(Boolean) : [];
                if (legacyBodies.length > 0) {
                    onChange(index, "slideBodies", legacyBodies);
                }
            }
            setSlideOpen(true);
        }
    };

    const collapseSlide = () => setSlideOpen(false);

    const resetSlide = () => {
        onChange(index, "slideBody", null);
        onChange(index, "slideBodies", []);
        setSlideOpen(false);
    };

    // Helpers for slide continuations
    const slideBodies = Array.isArray(section?.slideBodies) ? section.slideBodies : [];

    const addSlideContinuation = () => {
        onChange(index, "slideBodies", [...slideBodies, ""]);
    };

    const updateSlideContinuation = (partIdx, html) => {
        const next = [...slideBodies];
        next[partIdx] = html;
        onChange(index, "slideBodies", next);
    };

    const removeSlideContinuation = (partIdx) => {
        onChange(index, "slideBodies", slideBodies.filter((_, i) => i !== partIdx));
    };

    return (
        <div className={`section-card${slideOpen ? " section-card--split" : ""}`} aria-label={`Section #${number}`}>
            {/* Header row */}
            <div className="section-card__header">
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span className="section-card__label" aria-label={`Section #${number}`}>
                        Section #{number}
                    </span>
                    {hasCustomSlide && (
                        <span className="slide-custom-badge" title="This section has custom slide content">
                            {slideBodies.length > 0 ? `Custom Slide (${slideBodies.length + 1} slides)` : "Custom Slide"}
                        </span>
                    )}
                </div>

                <div className="section-card__controls">
                    {typeof onMoveUp === "function" && index > 0 && (
                        <button type="button" onClick={() => onMoveUp(index)} title="Move up">↑</button>
                    )}
                    {typeof onMoveDown === "function" && index < total - 1 && (
                        <button type="button" onClick={() => onMoveDown(index)} title="Move down">↓</button>
                    )}

                    {slideOpen ? (
                        <>
                            <button
                                type="button"
                                onClick={collapseSlide}
                                title="Collapse slide editor (keeps your custom content)"
                                className="btn-slide-active"
                            >
                                Collapse Slide
                            </button>
                            <button
                                type="button"
                                onClick={resetSlide}
                                title="Remove custom slide content and inherit from web"
                                className="btn-slide"
                                style={{ color: "#c0392b" }}
                            >
                                Reset Slide
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={openSlideEditor}
                            title="Edit what appears on the slideshow for this section"
                            className={hasCustomSlide ? "btn-slide-active" : "btn-slide"}
                        >
                            Edit Slide
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => typeof onAddCommentToSection === "function" ? onAddCommentToSection(index) : null}
                        aria-label="Add a comment for this section"
                        title="Add a comment for this section"
                    >
                        + Comment
                    </button>

                    {typeof onRemove === "function" && (
                        <button type="button" onClick={() => onRemove(index)} title="Remove section">
                            Remove
                        </button>
                    )}
                </div>
            </div>

            {/* Title input */}
            <input
                type="text"
                className="subscribe-input section-title-input"
                placeholder="Section title (optional)"
                value={section?.header || ""}
                onChange={(e) => onChange(index, "header", e.target.value)}
            />

            {slideOpen ? (
                /* Split view: web content left, slide content right */
                <div className="section-split-grid">
                    <div className="section-split-panel">
                        <div className="section-split-panel__label">
                            <span>Web Content</span>
                            <span className="section-split-panel__hint">Shown on the talk page</span>
                        </div>
                        <ReactQuill
                            theme="snow"
                            value={section?.body || ""}
                            onChange={(content, delta, source, editor) => onChange(index, "body", editor.getHTML())}
                            modules={quillModules}
                            formats={quillFormats}
                            className="quill-editor"
                        />
                    </div>
                    <div className="section-split-panel section-split-panel--slide">
                        <div className="section-split-panel__label">
                            <span>Slide Content</span>
                            <span className="section-split-panel__hint">Shown in the slideshow</span>
                        </div>

                        {/* Slide 1 (primary) */}
                        {slideBodies.length > 0 && (
                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2d5be3", marginBottom: "0.25rem" }}>
                                Slide 1
                            </div>
                        )}
                        <ReactQuill
                            theme="snow"
                            value={section?.slideBody || ""}
                            onChange={(content, delta, source, editor) => onChange(index, "slideBody", editor.getHTML())}
                            modules={quillModules}
                            formats={quillFormats}
                            className="quill-editor"
                        />

                        {/* Continuation slides */}
                        {slideBodies.map((part, partIdx) => (
                            <div key={partIdx} style={{ marginTop: "0.75rem", position: "relative", zIndex: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem", position: "relative", zIndex: 11 }}>
                                    <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#2d5be3" }}>
                                        Slide {partIdx + 2}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeSlideContinuation(partIdx)}
                                        title="Remove this slide"
                                        style={{ fontSize: "0.72rem", padding: "0.1rem 0.4rem", background: "#fff0f0", color: "#c0392b", border: "1px solid #f5c6c6", borderRadius: "4px", cursor: "pointer", position: "relative", zIndex: 12 }}
                                    >
                                        Remove
                                    </button>
                                </div>
                                <ReactQuill
                                    theme="snow"
                                    value={part || ""}
                                    onChange={(content, delta, source, editor) => updateSlideContinuation(partIdx, editor.getHTML())}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    className="quill-editor"
                                />
                            </div>
                        ))}

                        <div style={{ position: "relative", zIndex: 10, marginTop: "0.6rem" }}>
                            <button
                                type="button"
                                onClick={addSlideContinuation}
                                className="btn-slide"
                                style={{ fontSize: "0.8rem" }}
                                title="Add another slide for this section"
                            >
                                + Add Slide
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* Default view: just web content */
                <ReactQuill
                    theme="snow"
                    value={section?.body || ""}
                    onChange={(content, delta, source, editor) => onChange(index, "body", editor.getHTML())}
                    modules={quillModules}
                    formats={quillFormats}
                    className="quill-editor"
                />
            )}

            {/* Section comments */}
            {Array.isArray(comments) && comments.length > 0 && (
                <div className="talk-comments-list" style={{ marginTop: "0.75rem" }}>
                    {comments.map((c, i) => {
                        let dateStr = "";
                        if (c.createdAt && typeof c.createdAt === "object" && c.createdAt.seconds) {
                            dateStr = new Date(c.createdAt.seconds * 1000).toLocaleString();
                        } else if (typeof c.createdAt === "string") {
                            dateStr = new Date(c.createdAt).toLocaleString();
                        }
                        return (
                            <div
                                key={c.id || i}
                                className="talk-comment"
                                style={{ borderRadius: "10px", padding: "0.5rem 0.6rem", marginBottom: "0.35rem", background: "#ffffff" }}
                            >
                                <div className="talk-comment-header">
                                    <span className="talk-comment-name">{c.userName || "Anonymous"}</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginLeft: "auto" }}>
                                        {dateStr && <span className="talk-comment-date">{dateStr}</span>}
                                        {typeof onDeleteComment === "function" && c.id && (
                                            <button
                                                type="button"
                                                className="talk-comment-delete"
                                                onClick={() => onDeleteComment(section.sectionId || index, c.id)}
                                                title="Delete this comment"
                                                aria-label="Delete this comment"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <p className="talk-comment-text">{c.text}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Shared image URL / alt / caption inputs + constrained preview
function ImageInputs({ section, index, onChange }) {
    const hasUrl = (section?.imageUrl || "").trim() !== "";
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Image URL</label>
            <input
                type="text"
                placeholder="https://example.com/your-image.jpg"
                value={section?.imageUrl || ""}
                onChange={(e) => onChange?.(index, "imageUrl", e.target.value)}
                className="subscribe-input"
                style={{ width: "100%" }}
            />
            <label style={{ fontSize: "0.85rem", fontWeight: 600, marginTop: "0.25rem" }}>Caption (optional)</label>
            <input
                type="text"
                placeholder="Caption shown below the image"
                value={section?.imageCaption || ""}
                onChange={(e) => onChange?.(index, "imageCaption", e.target.value)}
                className="subscribe-input"
                style={{ width: "100%" }}
            />
            <label style={{ fontSize: "0.85rem", fontWeight: 600, marginTop: "0.25rem" }}>Alt text (for accessibility)</label>
            <input
                type="text"
                placeholder="Brief description of the image"
                value={section?.imageAlt || ""}
                onChange={(e) => onChange?.(index, "imageAlt", e.target.value)}
                className="subscribe-input"
                style={{ width: "100%" }}
            />
            {hasUrl && (
                <div style={{ marginTop: "0.5rem" }}>
                    <img
                        src={section.imageUrl}
                        alt="Preview"
                        style={{ display: "block", maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 6, border: "1px solid #e0e7f3" }}
                    />
                </div>
            )}
        </div>
    );
}

// Image Slide editor (bullets left, image right)
export function ImageSlideEditor({
    section,
    index,
    total,
    onChange,
    onRemove,
    onMoveUp,
    onMoveDown,
}) {
    const number = (typeof index === "number" ? index : 0) + 1;
    const hasCustomSlide = typeof section?.slideBody === "string" && section.slideBody !== null;
    const [slideOpen, setSlideOpen] = useState(hasCustomSlide);

    // Per-surface display: explicit choice, or derived from existing content.
    const talkMode = LAYOUT_MODES.includes(section?.talkLayout) ? section.talkLayout : talkImageMode(section);
    const slideMode = LAYOUT_MODES.includes(section?.slideLayout) ? section.slideLayout : slideImageMode(section);
    const showTalkText = talkMode !== "image";
    const showSlideText = slideMode !== "image";
    const bodyHint =
        showTalkText && showSlideText ? "(shown on the talk page and the slideshow)"
            : showTalkText ? "(shown on the talk page)"
                : showSlideText ? "(shown on the slideshow)"
                    : "";

    const openSlideEditor = () => {
        if (!slideOpen) {
            if (!hasCustomSlide) {
                onChange?.(index, "slideBody", section?.body || "");
            }
            setSlideOpen(true);
        }
    };
    const collapseSlide = () => setSlideOpen(false);
    const resetSlide = () => {
        onChange?.(index, "slideBody", null);
        setSlideOpen(false);
    };

    return (
        <div className="section-card" aria-label={`Image Slide #${number}`}>
            {/* Header row */}
            <div className="section-card__header">
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span className="section-card__label" aria-label={`Image Slide #${number}`}>
                        Image Slide
                    </span>
                    {hasCustomSlide && (
                        <span className="slide-custom-badge" title="This slide has custom slideshow content">
                            Custom Slide
                        </span>
                    )}
                </div>

                <div className="section-card__controls">
                    {typeof onMoveUp === "function" && index > 0 && (
                        <button type="button" onClick={() => onMoveUp(index)} title="Move up">↑</button>
                    )}
                    {typeof onMoveDown === "function" && index < total - 1 && (
                        <button type="button" onClick={() => onMoveDown(index)} title="Move down">↓</button>
                    )}

                    {slideOpen ? (
                        <>
                            <button
                                type="button"
                                onClick={collapseSlide}
                                title="Collapse slide editor (keeps your custom content)"
                                className="btn-slide-active"
                            >
                                Collapse Slide
                            </button>
                            <button
                                type="button"
                                onClick={resetSlide}
                                title="Remove custom slide content and inherit from web"
                                className="btn-slide"
                                style={{ color: "#c0392b" }}
                            >
                                Reset Slide
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={openSlideEditor}
                            title="Edit what appears on the slideshow separately from the talk page"
                            className={hasCustomSlide ? "btn-slide-active" : "btn-slide"}
                        >
                            Edit Slide
                        </button>
                    )}

                    {typeof onRemove === "function" && (
                        <button type="button" className="danger" onClick={() => onRemove(index)} title="Remove image slide">
                            Remove
                        </button>
                    )}
                </div>
            </div>

            {/* Slide Title */}
            <input
                type="text"
                className="subscribe-input section-title-input"
                placeholder="Slide title (optional)"
                value={section?.header || ""}
                onChange={(e) => onChange?.(index, "header", e.target.value)}
                style={{ marginBottom: "0.5rem" }}
            />

            {/* Per-surface display */}
            <div className="image-slide-modes">
                <div className="image-slide-mode-row">
                    <span className="image-slide-mode-label">Talk page</span>
                    <LayoutSegControl value={talkMode} onChange={(v) => onChange?.(index, "talkLayout", v)} />
                </div>
                <div className="image-slide-mode-row">
                    <span className="image-slide-mode-label">Slideshow</span>
                    <LayoutSegControl value={slideMode} onChange={(v) => onChange?.(index, "slideLayout", v)} />
                </div>
            </div>

            {slideOpen ? (
                /* Split view: talk page text left, slide text right, image below slide text */
                <div className="section-split-grid">
                    <div className="section-split-panel">
                        <div className="section-split-panel__label">
                            <span>Talk Page Text</span>
                        </div>
                        {showTalkText ? (
                            <ReactQuill
                                theme="snow"
                                value={section?.body || ""}
                                onChange={(content, delta, source, editor) => onChange?.(index, "body", editor.getHTML())}
                                modules={quillModules}
                                formats={quillFormats}
                                className="quill-editor"
                            />
                        ) : (
                            <div className="image-slide-mode-hint">Talk page shows the image only.</div>
                        )}
                    </div>
                    <div className="section-split-panel section-split-panel--slide">
                        <div className="section-split-panel__label">
                            <span>Slideshow Text</span>
                            <span className="section-split-panel__hint">(leave blank to reuse the talk page text)</span>
                        </div>
                        {showSlideText ? (
                            <ReactQuill
                                theme="snow"
                                value={section?.slideBody || ""}
                                onChange={(content, delta, source, editor) => onChange?.(index, "slideBody", editor.getHTML())}
                                modules={quillModules}
                                formats={quillFormats}
                                className="quill-editor"
                            />
                        ) : (
                            <div className="image-slide-mode-hint">Slideshow shows the image only.</div>
                        )}
                        <div style={{ marginTop: "0.75rem" }}>
                            <ImageInputs section={section} index={index} onChange={onChange} />
                        </div>
                    </div>
                </div>
            ) : (
                /* Default view: text + image side by side */
                <div className="image-slide-grid">
                    <div className="image-slide-left">
                        <label style={{ display: "block", fontSize: "0.85rem", margin: "0 0 0.25rem", fontWeight: 600 }}>
                            Notes / bullets
                            <span style={{ fontWeight: 400, marginLeft: "0.4rem", opacity: 0.6 }}>{bodyHint}</span>
                        </label>
                        {showTalkText || showSlideText ? (
                            <ReactQuill
                                theme="snow"
                                value={section?.body || ""}
                                onChange={(content, delta, source, editor) => onChange?.(index, "body", editor.getHTML())}
                                modules={quillModules}
                                formats={quillFormats}
                                className="quill-editor"
                            />
                        ) : (
                            <div className="image-slide-mode-hint">
                                Image only on both the talk page and the slideshow — no text needed.
                            </div>
                        )}
                    </div>
                    <div className="image-slide-right">
                        <ImageInputs section={section} index={index} onChange={onChange} />
                    </div>
                </div>
            )}
        </div>
    );
}

// Fixed, final-card editor for the talk's References list (supports continuations)
export function ReferencesEditor({ value, bodies = [], onChange, helperText }) {
    const [parts, setParts] = useState([typeof value === "string" ? value : "", ...Array.isArray(bodies) ? bodies : []]);
    const [touched, setTouched] = useState(false);
    const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);

    // Keep parts in sync with incoming value/bodies when props change
    useEffect(() => {
        const incoming = [
            typeof value === "string" ? value : "",
            ...Array.isArray(bodies) ? bodies : [],
        ];
        // Only update if the new props differ from current state
        const same = JSON.stringify(incoming) === JSON.stringify(parts);
        if (!same) {
            setParts(incoming);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, JSON.stringify(bodies)]);

    // Slightly simplified toolbar focused on citations formatting
    const referencesModules = {
        toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "super" }],
            ["link"],
            ["clean"],
        ],
    };

    const referencesFormats = [
        "bold",
        "italic",
        "underline",
        "list",
        "bullet",
        "script",
        "link",
        "clean",
    ];

    const emitChange = (nextParts) => {
        const first = nextParts[0] || "";
        const rest = nextParts.slice(1);
        onChange?.(first, rest);
    };

    const addContinuation = () => {
        setTouched(true);
        const next = [...parts, ""];
        setParts(next);
        emitChange(next);
    };

    const removeContinuationAt = (i) => {
        setTouched(true);
        const next = parts.filter((_, idx) => idx !== i);
        // Ensure at least one part exists
        if (next.length === 0) next.push("");
        setParts(next);
        emitChange(next);
    };

    const removeAllContinuations = () => {
        setTouched(true);
        const next = [parts[0] || ""];
        setParts(next);
        emitChange(next);
    };

    return (
        <div className="section-card" aria-label="References">
            <div className="section-card__header">
                <span className="section-card__label" aria-label="References">References</span>
                <div className="section-card__controls">
                    <button
                        type="button"
                        onClick={addContinuation}
                        aria-label="Add continuation (adds another references part)"
                        title="Add continuation (adds another references part)"
                        className="slideshow-button"
                    >
                        + Continue
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsGuidelinesOpen(true)}
                        aria-label="View citation & formatting rules"
                        title="View citation & formatting rules"
                        className="slideshow-button"
                    >
                        Guidelines
                    </button>
                    {parts.length > 1 && (
                        <button
                            type="button"
                            onClick={removeAllContinuations}
                            aria-label="Remove all continuations"
                            title="Remove all continuations"
                            className="slideshow-button btn-danger"
                        >
                            × Remove All
                        </button>
                    )}
                </div>
            </div>

            {helperText && (
                <div style={{ fontSize: "0.9rem", color: "#4f5b66", margin: "0.25rem 0 0.5rem" }}>
                    {helperText}
                </div>
            )}

            {parts.map((html, i) => (
                <React.Fragment key={i}>
                    {parts.length > 1 && (
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, opacity: 0.7, margin: i === 0 ? "0.25rem 0" : "0.75rem 0 0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            {`References Part ${String.fromCharCode(65 + i)}`}
                            {i > 0 && (
                                <button
                                    type="button"
                                    onClick={() => removeContinuationAt(i)}
                                    title="Remove this continuation"
                                    aria-label="Remove this continuation"
                                    style={{ fontSize: "0.75rem" }}
                                    className="slideshow-button btn-danger"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    )}
                    <ReactQuill
                        theme="snow"
                        value={html || ""}
                        onChange={(content, delta, source, editor) => {
                            setTouched(true);
                            const newHTML = editor.getHTML();
                            const next = parts.slice();
                            next[i] = newHTML;
                            setParts(next);
                            emitChange(next);
                        }}
                        modules={referencesModules}
                        formats={referencesFormats}
                        className="quill-editor"
                        placeholder={i === 0 ? "Enter a numbered list of references. Use hyperlinks on titles/sources; avoid raw URLs." : "Additional references (continued)"}
                    />
                </React.Fragment>
            ))}
            <GuidelinesModal
                isOpen={isGuidelinesOpen}
                onClose={() => setIsGuidelinesOpen(false)}
            />
        </div>
    );
}
